/* Product page: variant selection, gallery, zoom, sticky buy bar, share, recommendations. */
(() => {
  'use strict';
  const Purr = window.Purr;

  /* ---------- Variant picker + form ---------- */
  if (!customElements.get('purr-product-form')) {
    class PurrProductForm extends HTMLElement {
      connectedCallback() {
        if (this._ready) return; this._ready = true;
        this.section = this.closest('[data-section-id]');
        this.sectionId = this.dataset.sectionId;
        this.form = document.getElementById(this.dataset.formId);
        try { this.variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]'); } catch (e) { this.variants = []; }
        this.options = Array.from(this.querySelectorAll('.purr-option'));
        this.addEventListener('change', (e) => { if (e.target.matches('[data-option-value]')) this.onOptionChange(); });
        this.updateOptionAvailability();
        this.gallery = this.section && this.section.querySelector('purr-gallery');
      }
      selectedOptions() {
        return this.options.map((fs) => { const checked = fs.querySelector('[data-option-value]:checked'); return checked ? checked.value : null; });
      }
      currentVariant() {
        const sel = this.selectedOptions();
        return this.variants.find((v) => v.options.every((o, i) => o === sel[i]));
      }
      onOptionChange() {
        this.options.forEach((fs) => { const checked = fs.querySelector('[data-option-value]:checked'); const label = fs.querySelector('[data-option-selected]'); if (checked && label) label.textContent = checked.value; });
        const variant = this.currentVariant();
        this.updateOptionAvailability();
        const idInput = this.form && this.form.querySelector('[data-variant-id]');
        const addBtn = this.form && this.form.querySelector('[data-add-button]');
        const sticky = document.querySelector('[data-sticky-add]');
        if (!variant) {
          if (idInput) idInput.disabled = true;
          if (addBtn) { addBtn.disabled = true; const l = addBtn.querySelector('[data-label]'); if (l) l.textContent = Purr.strings.unavailable; }
          if (sticky) sticky.disabled = true;
          return;
        }
        if (idInput) { idInput.value = variant.id; idInput.disabled = false; }
        if (this.dataset.updateUrl !== 'false' && history.replaceState) {
          const u = new URL(window.location.href); u.searchParams.set('variant', variant.id); history.replaceState({}, '', u.toString());
        }
        if (variant.featured_media && this.gallery) this.gallery.goTo(String(variant.featured_media.id));
        this.refreshSection(variant);
        Purr.emit('purr:variant:change', { variant, sectionId: this.sectionId });
      }
      updateOptionAvailability() {
        const sel = this.selectedOptions();
        this.options.forEach((fs, i) => {
          fs.querySelectorAll('[data-option-value]').forEach((input) => {
            const test = sel.slice(); test[i] = input.value;
            const exists = this.variants.some((v) => v.options.every((o, j) => (test[j] === null ? true : o === test[j])));
            const available = this.variants.some((v) => v.available && v.options.every((o, j) => (test[j] === null ? true : o === test[j])));
            const label = fs.querySelector('label[for="' + input.id + '"]');
            if (label) { label.classList.toggle('is-unavailable', !available); label.classList.toggle('is-missing', !exists); }
          });
        });
      }
      async refreshSection(variant) {
        const url = new URL(this.dataset.url, window.location.origin);
        url.searchParams.set('variant', variant.id);
        url.searchParams.set('section_id', this.sectionId);
        this.section && this.section.setAttribute('aria-busy', 'true');
        try {
          const res = await fetch(url.toString());
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          ['price', 'buy', 'availability', 'price-compact', 'sku', 'inventory'].forEach((key) => {
            const from = doc.querySelector('[data-variant-swap="' + key + '"]');
            const to = this.section.querySelector('[data-variant-swap="' + key + '"]');
            if (from && to) to.innerHTML = from.innerHTML;
          });
          const sticky = document.querySelector('[data-sticky-add]');
          if (sticky) sticky.disabled = !variant.available;
        } catch (e) { /* keep current UI */ }
        finally { this.section && this.section.removeAttribute('aria-busy'); }
      }
    }
    customElements.define('purr-product-form', PurrProductForm);
  }

  /* ---------- Gallery ---------- */
  if (!customElements.get('purr-gallery')) {
    class PurrGallery extends HTMLElement {
      connectedCallback() {
        if (this._ready) return; this._ready = true;
        this.layout = this.dataset.layout || 'thumbnails_left';
        this.track = this.querySelector('[data-gallery-track]');
        this.slides = Array.from(this.querySelectorAll('.purr-gallery__slide'));
        this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
        this.dots = this.querySelector('[data-dots]');
        if (!this.track) return;
        this.thumbs.forEach((t) => t.addEventListener('click', () => this.goTo(t.dataset.mediaId)));
        if (this.dots) {
          this.slides.forEach((s, i) => { const b = document.createElement('button'); b.type = 'button'; b.className = 'purr-carousel__dot'; b.setAttribute('aria-label', String(i + 1)); b.addEventListener('click', () => this.goTo(s.dataset.mediaId)); this.dots.appendChild(b); });
        }
        this.track.addEventListener('scroll', Purr.throttle(() => this.syncFromScroll(), 100), { passive: true });
        const active = this.slides.find((s) => s.hasAttribute('data-active')) || this.slides[0];
        if (active) this.setActive(active, false);
        // Zoom buttons
        this.querySelectorAll('[data-zoom-open]').forEach((btn) => btn.addEventListener('click', () => {
          const dialogHost = this.closest('.purr-product__gallery')?.querySelector('purr-dialog');
          if (!dialogHost) return;
          dialogHost.open(btn);
          const item = dialogHost.querySelector('[data-media-id="' + btn.dataset.mediaId + '"]');
          if (item) requestAnimationFrame(() => item.scrollIntoView({ block: 'start', behavior: 'auto' }));
        }));
      }
      isDesktop() { return window.matchMedia('(min-width: 1024px)').matches; }
      /* "Active" mode: one visible slide switched via thumbnails (desktop, thumbnails_left layout). */
      usesActive() { return this.isDesktop() && this.layout === 'thumbnails_left'; }
      isStacked() { return this.isDesktop() && this.layout === 'stacked'; }
      goTo(mediaId) {
        const slide = this.slides.find((s) => s.dataset.mediaId === String(mediaId));
        if (!slide) return;
        if (this.usesActive()) this.setActive(slide, true);
        else if (this.isStacked()) { this.setActive(slide, false); slide.scrollIntoView({ block: 'nearest', behavior: Purr.reduceMotion() ? 'auto' : 'smooth' }); }
        else this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: Purr.reduceMotion() ? 'auto' : 'smooth' });
      }
      setActive(slide, scroll) {
        this.slides.forEach((s) => { const on = s === slide; s.toggleAttribute('data-active', on); });
        this.thumbs.forEach((t) => { const on = t.dataset.mediaId === slide.dataset.mediaId; if (on) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current'); });
        if (this.dots) Array.from(this.dots.children).forEach((d, i) => d.classList.toggle('is-active', this.slides[i] === slide));
        if (scroll && !this.usesActive() && !this.isStacked()) this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: 'auto' });
      }
      syncFromScroll() {
        if (this.usesActive() || this.isStacked()) return;
        const x = this.track.scrollLeft + this.track.clientWidth / 2;
        let best = this.slides[0], dist = Infinity;
        this.slides.forEach((s) => { const c = s.offsetLeft - this.track.offsetLeft + s.clientWidth / 2; const d = Math.abs(c - x); if (d < dist) { dist = d; best = s; } });
        if (best && !best.hasAttribute('data-active')) this.setActive(best, false);
      }
    }
    customElements.define('purr-gallery', PurrGallery);
  }

  /* ---------- Sticky buy bar ---------- */
  const initSticky = () => {
    const bar = document.querySelector('[data-sticky-buy]');
    // The add button lives inside [data-variant-swap="buy"], whose innerHTML is replaced on every variant
    // change – observe the stable wrapper and re-query the button on demand.
    const getAddBtn = () => document.querySelector('[data-add-button]');
    const firstBtn = getAddBtn();
    const anchor = (firstBtn && firstBtn.closest('[data-variant-swap="buy"]')) || firstBtn;
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      const visible = entries.some((en) => en.isIntersecting);
      const past = anchor.getBoundingClientRect().top < 0;
      const show = !visible && past;
      bar.hidden = !show; bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      document.body.classList.toggle('purr-has-sticky-buy', show);
    }, { threshold: 0 });
    io.observe(anchor);
    bar.querySelector('[data-sticky-add]')?.addEventListener('click', () => {
      const addBtn = getAddBtn();
      const form = (addBtn && addBtn.closest('form')) || anchor.closest('form');
      if (form) { if (typeof form.requestSubmit === 'function') form.requestSubmit(); else if (addBtn) addBtn.click(); else form.submit(); }
    });
  };

  /* ---------- Share ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-share]');
    if (!btn) return;
    const data = { title: btn.dataset.shareTitle, url: btn.dataset.shareUrl || window.location.href };
    if (navigator.share) { try { await navigator.share(data); } catch (err) { /* cancelled */ } return; }
    try { await navigator.clipboard.writeText(data.url); Purr.toast(btn.dataset.copiedText || 'Link kopiert'); } catch (err) { Purr.toast(data.url, { type: 'info', timeout: 6000 }); }
  });

  /* ---------- Recommendations (Section Rendering) ---------- */
  const initRecommendations = () => {
    document.querySelectorAll('[data-recommendations][data-url]').forEach(async (el) => {
      if (el.dataset.loaded) return; el.dataset.loaded = 'true';
      try {
        const res = await fetch(el.dataset.url);
        if (!res.ok) throw new Error('recs');
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const inner = doc.querySelector('[data-recommendations]');
        if (inner && inner.innerHTML.trim()) { el.innerHTML = inner.innerHTML; el.hidden = false; Purr.initReveal && Purr.initReveal(); }
        else el.remove();
      } catch (err) { el.remove(); }
    });
  };

  Purr.ready(() => { initSticky(); initRecommendations(); });
  document.addEventListener('shopify:section:load', initRecommendations);
})();
